export type GroupableSurveyElement = {
  type?: string;
  name?: string;
  title?: string | { default?: string };
  visibleIf?: string;
  firestoreListId?: string;
  dataListId?: string;
  elements?: GroupableSurveyElement[];
  templateElements?: GroupableSurveyElement[];
};

export type GroupableSurveyJSON = {
  pages?: { elements?: GroupableSurveyElement[] }[];
};

export type NetworkQuestionGroupMember = {
  name: string;
  country: string;
  listId?: string;
};

export type NetworkQuestionGroup = {
  id: string;
  baseName: string;
  title: string;
  controllerName: string;
  controllerInPanel: boolean;
  parentName?: string;
  members: NetworkQuestionGroupMember[];
};

const GROUP_ID_PREFIX = "__group__";

export const isNetworkQuestionGroupId = (value?: string) =>
  typeof value === "string" && value.startsWith(GROUP_ID_PREFIX);

const slugCountry = (country: string) =>
  country
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getTitleText = (el: GroupableSurveyElement) => {
  if (typeof el.title === "string") return el.title.trim();
  if (el.title && typeof el.title === "object") return String(el.title.default || "").trim();
  return "";
};

const normalizeTitle = (title: string) =>
  title
    .replace(/^q\d+\s*/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const parseVisibleIfCountry = (visibleIf?: string): { controllerName: string; controllerInPanel: boolean; country: string } | null => {
  if (!visibleIf) return null;
  const match = visibleIf.trim().match(/^\{(panel\.)?([^}]+)\}\s*=\s*(['"])(.*?)\3$/);
  if (!match) return null;
  return {
    controllerName: match[2],
    controllerInPanel: Boolean(match[1]),
    country: match[4],
  };
};

const makeGroupId = (parentName: string | undefined, baseName: string, controllerName: string) =>
  `${GROUP_ID_PREFIX}${parentName || "root"}__${baseName}__${controllerName}`;

export function collectNetworkQuestionGroups(survey: GroupableSurveyJSON | undefined): NetworkQuestionGroup[] {
  if (!survey?.pages) return [];

  type Draft = {
    id: string;
    baseName: string;
    title: string;
    titleKey: string;
    controllerName: string;
    controllerInPanel: boolean;
    parentName?: string;
    members: NetworkQuestionGroupMember[];
  };

  const drafts = new Map<string, Draft>();

  const visit = (el: GroupableSurveyElement, parentName?: string) => {
    const type = (el.type || "").toLowerCase();
    if (type === "dropdown" && el.name) {
      const listId = el.firestoreListId || el.dataListId;
      const parsed = parseVisibleIfCountry(el.visibleIf);
      if (parsed) {
        const suffix = slugCountry(parsed.country);
        const expectedSuffix = `_${suffix}`;
        if (suffix && el.name.endsWith(expectedSuffix)) {
          const baseName = el.name.slice(0, -expectedSuffix.length);
          const title = getTitleText(el) || baseName;
          const titleKey = normalizeTitle(title);
          const mapKey = [
            parentName || "root",
            baseName,
            parsed.controllerName,
            parsed.controllerInPanel ? "panel" : "root",
            titleKey,
          ].join("::");
          const existing =
            drafts.get(mapKey) || {
              id: makeGroupId(parentName, baseName, parsed.controllerName),
              baseName,
              title,
              titleKey,
              controllerName: parsed.controllerName,
              controllerInPanel: parsed.controllerInPanel,
              parentName,
              members: [],
            };
          existing.members.push({ name: el.name, country: parsed.country, listId });
          drafts.set(mapKey, existing);
        }
      }
    }

    const nextParent = type === "paneldynamic" || type === "panel" ? el.name || parentName : parentName;
    el.elements?.forEach((child) => visit(child, nextParent));
    el.templateElements?.forEach((child) => visit(child, nextParent));
  };

  survey.pages.forEach((page) => page.elements?.forEach((el) => visit(el)));

  return Array.from(drafts.values())
    .filter((group) => group.members.length > 1)
    .map((group) => ({
      id: group.id,
      baseName: group.baseName,
      title: group.title,
      controllerName: group.controllerName,
      controllerInPanel: group.controllerInPanel,
      parentName: group.parentName,
      members: [...group.members].sort((a, b) => a.country.localeCompare(b.country)),
    }));
}

export const findNetworkQuestionGroup = (
  survey: GroupableSurveyJSON | undefined,
  groupId: string | undefined
) => collectNetworkQuestionGroups(survey).find((group) => group.id === groupId);

export const formatGroupedOrganizationLabel = (name: string, country: string) => {
  const cleanName = name.replace(/\s+/g, " ").trim();
  const cleanCountry = country.replace(/\s+/g, " ").trim();
  if (!cleanName) return "";
  return cleanCountry ? `${cleanName} (${cleanCountry})` : cleanName;
};
